/**
 * Faq.js
 *
 * Frequently asked questions page.
 *
 * Route: /faq
 */

import React, { useState } from 'react';
import { PAYPAL_PERCENTAGE_FEE, PAYPAL_FIXED_FEE } from './utils/constants';
import Navigation from './components/Navigation';
import paypalYoureAllSetImg from './faq/paypal-youre-all-set.png';
import paypalWaitingImg from './faq/paypal-waiting.png';
import paypalConnectedImg from './faq/paypal-connected.png';

function FaqItem({ question, children, defaultOpen = false }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-gray-200 py-4">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-start justify-between gap-4 text-left group"
        aria-expanded={open}
      >
        <span className="font-semibold text-gray-900 group-hover:text-indigo-600 transition-colors">
          {question}
        </span>
        <span className={`flex-shrink-0 mt-0.5 text-gray-400 transition-transform ${open ? 'rotate-180' : ''}`}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
            <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.06l3.71-3.83a.75.75 0 111.08 1.04l-4.25 4.39a.75.75 0 01-1.08 0L5.21 8.27a.75.75 0 01.02-1.06z" clipRule="evenodd" />
          </svg>
        </span>
      </button>
      {open && (
        <div className="mt-3 text-gray-700 text-sm sm:text-base leading-relaxed">
          {children}
        </div>
      )}
    </div>
  );
}

function FaqCategory({ title, children }) {
  return (
    <section>
      <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3">{title}</h2>
      <div>{children}</div>
    </section>
  );
}

function Faq() {
  return (
    <div className="min-h-screen bg-gray-50">
      <Navigation />

      <div className="max-w-2xl mx-auto px-4 py-12">
        <div className="mb-10">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Frequently asked questions</h1>
          <p className="text-gray-500 text-sm">
            Quick answers about giving, receiving, and using Gifted. Still stuck?{' '}
            <a href="mailto:giftedbeta1@gmail.com" className="text-indigo-600 hover:text-indigo-700 underline">
              Email us
            </a>
            .
          </p>
        </div>

        <div className="space-y-10">

          <FaqCategory title="Receiving gifts">
            <FaqItem question="Is anything deducted from my gift?">
              <p>
                No, you receive 100% of the amount the gifter intended for you. PayPal's
                processing fee is covered by the gifter on top of the gift amount, so nothing
                is taken out of what reaches your PayPal account.
              </p>
            </FaqItem>

            <FaqItem question="When will the money arrive in my PayPal account?">
              <p className="mb-3">
                <strong>Direct gifts</strong> (sent to you personally through your profile page) are released to your PayPal
                account shortly after the gifter completes payment - no waiting period.
              </p>
              <p>
                <strong>Gift pools</strong> go through a brief review period of up to three days
                before disbursement. During that window, PayPal holds the funds while Gifted
                monitors for any red flags, alongside PayPal's own identity verification and fraud
                protection. Once review is complete, Gifted releases the funds to the recipient's
                PayPal account - no action required on your end. This moderation period helps ensure
                a safe and trustworthy community where generosity can thrive and gifters can give with
                confidence and peace of mind.
              </p>
            </FaqItem>

            <FaqItem question="How can I make my gift pool more likely to succeed?">
              <p className="mb-3">It's all about building trust so others feel confident gifting you. Here's what works:</p>
              <ul className="space-y-3">
                <li className="flex items-start gap-2">
                  <span className="mt-0.5">🛡️</span>
                  <span><strong>Get vouched.</strong> Ask someone who knows you personally to vouch for you by tapping the shield button on your post. It's one of the strongest signals that your need is legitimate and trustworthy.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-0.5">📸</span>
                  <span><strong>Add photos and documentation.</strong> Show your situation - a photo of a medical bill, lease notice, or the thing you need helps gifters feel confident they're helping with something real.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-0.5">✍️</span>
                  <span><strong>Be specific.</strong> Explain exactly what you need and how the funds will be used. "Cover a $100 rent gap before Friday" is clearer than "need help with bills."</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-0.5">🙎🏽‍♀️</span>
                  <span><strong>Make it personal.</strong> A clear profile photo and a real name help gifters feel they're supporting a real person. Write in your own voice - AI-generated posts can feel generic and are often easy to spot. You can use tools for proofreading, but take the time to share your story in your own words. Authenticity builds trust.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-0.5">💸</span>
                  <span><strong>Connect PayPal first.</strong> Gifters can't contribute until your PayPal is connected. Set it up in Settings before posting so your pool opens immediately.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-0.5">🔗</span>
                  <span><strong>Share your post.</strong> Send your post link to people who know you. The first few contributions and vouches from your own network make strangers much more likely to give.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-0.5">💬</span>
                  <span><strong>Respond to questions.</strong> Answering questions or adding updates builds trust and helps others feel comfortable contributing.</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="mt-0.5">🤝</span>
                  <span><strong>Pay it forward.</strong> Gift others on Gifted. Your history of giving appears on your profile and makes others more likely to help you in return.</span>
                </li>
              </ul>
            </FaqItem>

            <FaqItem question="Does Gifted ever touch the money?">
              <p>
                No. Gifted doesn't hold, manage, or have access to anyone's money at any point.
                Funds move directly from the gifter to the recipient's PayPal account.
              </p>
            </FaqItem>
          </FaqCategory>

          <FaqCategory title="Giving gifts">
            <FaqItem question="Who pays the PayPal processing fee?">
              <p className="mb-3">
                The gifter covers it. When you give, the total is grossed up so that after PayPal
                takes its cut ({(PAYPAL_PERCENTAGE_FEE * 100).toFixed(1)}% + ${PAYPAL_FIXED_FEE} per
                transaction), the recipient still receives the full amount you intended.
              </p>
              <p>
                The breakdown is shown upfront at checkout — no surprises.
              </p>
            </FaqItem>

            <FaqItem question="What if the recipient doesn't have a PayPal account?">
              <p>
                Right now, the recipient needs to sign up on Gifted and connect their PayPal account
                before they can receive a gift. If they haven't, you'll see a message at checkout
                asking you to let them know to sign up first.
              </p>
            </FaqItem>

            <FaqItem question="Can I give from a non-US PayPal account?">
              <p>
                Yes. All gifts are currently processed in US dollars (USD), and PayPal will convert your
                local currency at checkout.
              </p>
            </FaqItem>
          </FaqCategory>

          <FaqCategory title="PayPal onboarding troubleshooting">
            <FaqItem
              question={`After connecting PayPal, the "Return to Gifted" button doesn't work. What do I do?`}
              defaultOpen={true}
            >
              <p className="mb-3">
                We're aware the "Return to Gifted" button in the PayPal popup isn't always
                responsive at this time. If that's what you're experiencing,
                close the "You're all set!" popup (press the X on the window bar), then tap
                <strong> "I've connected my PayPal account"</strong> on the Gifted waiting screen.
                Your PayPal should then be connected.
              </p>
              <p className="mb-3">Here's the full step-by-step:</p>
              <ol className="list-decimal pl-5 space-y-4">
                <li>
                  From your Settings page, click <strong>Connect with PayPal</strong> (or use the
                  blue "Connect PayPal" button in the banner at the top of the landing page). A
                  PayPal popup will open.
                </li>
                <li>
                  In the PayPal popup, either log in with your existing PayPal account or enter a
                  personal email to create one - PayPal will walk you through the setup.
                </li>
                <li>
                  Click <strong>Allow</strong> to connect your PayPal account to Gifted. You'll see
                  a "You're all set!" screen. If the <strong>Return to Gifted</strong> button doesn't respond,
                  simply close the popup using the X on the window bar:
                  <img
                    src={paypalYoureAllSetImg}
                    alt="PayPal 'You're all set!' confirmation screen with a 'Return to Gifted' button"
                    className="mt-3 rounded-lg border border-gray-200 max-w-xs w-full h-auto"
                  />
                </li>
                <li>
                  After closing the PayPal popup, you may see a "Waiting for PayPal connection"
                  screen on Gifted (especially if you're on a mobile device).
                  Tap <strong>"I've connected my PayPal account"</strong>:
                  <img
                    src={paypalWaitingImg}
                    alt="Gifted Payment Settings showing 'Waiting for PayPal connection in the popup window' with an 'I've connected my PayPal account' button"
                    className="mt-3 rounded-lg border border-gray-200 max-w-xs w-full h-auto"
                  />
                </li>
                <li>
                  You should now see "PayPal Connected" under Payment Settings on your Settings page:
                  <img
                    src={paypalConnectedImg}
                    alt="Gifted Settings page showing 'PayPal account connected successfully' and 'PayPal Connected' status"
                    className="mt-3 rounded-lg border border-gray-200 max-w-md w-full h-auto"
                  />
                </li>
              </ol>
              <p className="mt-3">
                If you still don't see "PayPal Connected" after these steps, reach out and
                we'll help sort it out.
              </p>
            </FaqItem>
          </FaqCategory>

        </div>

        <div className="mt-12 text-sm text-gray-500">
          Still have questions?{' '}
          <a href="mailto:giftedbeta1@gmail.com" className="text-indigo-600 hover:text-indigo-700 underline">
            giftedbeta1@gmail.com
          </a>
        </div>
      </div>
    </div>
  );
}

export default Faq;
